import React, { useState, useEffect } from 'react';
import {
  Download,
  Database,
  Users,
  Building2,
  FileText,
  Phone,
  CreditCard,
  Layers,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  GraduationCap,
  ClipboardList,
  ClipboardCheck,
  BarChart3,
  FileSpreadsheet,
  HandCoins,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/services/apiClient';
import { adminService, type BonusReportCycle } from '@/services/api/adminService';
import Toast, { ToastType } from '@/components/ui/Toast';
import { useAuth } from '@/providers/AuthProvider';
import { hasPermission, hasRole } from '@/auth/permissions';
import { EXPORT_KEYS, EXPORT_ROLES, EXPORT_KEY_ROLES, RAW_FO_ROLES } from '@/lib/exportPermissions';

interface EntityInfo {
  key: string;
  name: string;
  description: string;
  fo_endpoint: string;
  estimated_fields: number;
}

type DownloadStatus = 'idle' | 'downloading' | 'done' | 'error';

const ENTITY_ICONS: Record<string, React.ReactNode> = {
  Workers: <Users className="w-6 h-6" />,
  EmploymentsV2: <Building2 className="w-6 h-6" />,
  Positions: <Layers className="w-6 h-6" />,
  EmploymentDetails: <FileText className="w-6 h-6" />,
  WorkerContacts: <Phone className="w-6 h-6" />,
  PersonIdentificationNumbers: <CreditCard className="w-6 h-6" />,
};

const FODataExportPage: React.FC = () => {
  const { user } = useAuth();
  // Which cards to render: the grant, OR a role that already implies THAT export.
  // The roles differ per export (Staff List is tighter on the server), so this
  // reads the per-key map rather than one blanket role set — otherwise an hrbp
  // would be shown a Staff List button that 403s.
  const can = (key: string) =>
    hasPermission(user, key, EXPORT_KEY_ROLES[key] ?? EXPORT_ROLES);
  // The raw F&O entity dumps include bank accounts and ID numbers, so they are
  // role-only and deliberately have no grantable key (matches the server).
  const canSeeRawFo = hasRole(user, RAW_FO_ROLES);

  const [entities, setEntities] = useState<EntityInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadStatus, setDownloadStatus] = useState<Record<string, DownloadStatus>>({});
  const [mergedStatus, setMergedStatus] = useState<DownloadStatus>('idle');
  const [professionalExportStatus, setProfessionalExportStatus] = useState<DownloadStatus>('idle');
  const [lineManagerExportStatus, setLineManagerExportStatus] = useState<DownloadStatus>('idle');
  const [onboardingObjExportStatus, setOnboardingObjExportStatus] = useState<DownloadStatus>('idle');
  const [positionGradeExportStatus, setPositionGradeExportStatus] = useState<DownloadStatus>('idle');
  const [headcountExportStatus, setHeadcountExportStatus] = useState<DownloadStatus>('idle');
  const [staffListStatus, setStaffListStatus] = useState<DownloadStatus>('idle');
  const [checkInCalibStatus, setCheckInCalibStatus] = useState<DownloadStatus>('idle');
  // Year-End counts the ring-fenced population; either period leaves out Too Soon To Rate.
  const [checkInCalibPeriod, setCheckInCalibPeriod] = useState<'midyear' | 'yearend'>(new Date().getMonth() >= 6 ? 'yearend' : 'midyear');
  const [bonusReportStatus, setBonusReportStatus] = useState<DownloadStatus>('idle');
  // Review cycles the Bonus report can take its rating from; null while loading.
  const [bonusCycles, setBonusCycles] = useState<BonusReportCycle[] | null>(null);
  const [bonusCyclesFailed, setBonusCyclesFailed] = useState(false);
  const [bonusCycleKey, setBonusCycleKey] = useState('');
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const canBonusReport = can(EXPORT_KEYS.bonusReport);

  useEffect(() => {
    // Only fetch the raw F&O catalogue if this user may actually see that
    // section. It renders below the export cards, but the fetch is page-level:
    // without this guard a user who was granted a single export 403s on every
    // page load and gets two error toasts for a section they can't even see.
    if (!canSeeRawFo) {
      setLoading(false);
      return;
    }
    const fetchEntities = async () => {
      try {
        const response = await apiClient.get<{ entities: EntityInfo[] }>(
          '/api/v1/integration/fo-export/entities'
        );
        setEntities(response.data.entities);
        const initial: Record<string, DownloadStatus> = {};
        response.data.entities.forEach((e) => (initial[e.key] = 'idle'));
        setDownloadStatus(initial);
      } catch (error) {
        console.error('Failed to load entity catalog:', error);
        setToast({ message: 'Failed to load entity catalog', type: 'error' });
      } finally {
        setLoading(false);
      }
    };
    fetchEntities();
  }, [canSeeRawFo]);

  useEffect(() => {
    // Same rule as the catalogue above: this runs at page level, so only for
    // someone who can see the Bonus report card — anyone else would 403 here on
    // every visit.
    if (!canBonusReport) return;
    let cancelled = false;
    adminService
      .getBonusReportCycles()
      .then((cycles) => {
        if (cancelled) return;
        setBonusCycles(cycles);
        if (cycles.length) setBonusCycleKey(`${cycles[0].review_type}:${cycles[0].year}`);
      })
      .catch((error) => {
        console.error('Failed to load review cycles:', error);
        if (!cancelled) setBonusCyclesFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canBonusReport]);

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownloadEntity = async (entityKey: string) => {
    setDownloadStatus((prev) => ({ ...prev, [entityKey]: 'downloading' }));
    try {
      const response = await apiClient.get(
        `/api/v1/integration/fo-export/entity/${entityKey}`,
        { responseType: 'blob', timeout: 600000 }
      );

      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : `${entityKey}.xlsx`;

      triggerBlobDownload(response.data as Blob, filename);

      setDownloadStatus((prev) => ({ ...prev, [entityKey]: 'done' }));
      setToast({ message: `${entityKey} downloaded successfully`, type: 'success' });

      setTimeout(() => {
        setDownloadStatus((prev) => ({ ...prev, [entityKey]: 'idle' }));
      }, 5000);
    } catch (error) {
      console.error(`Download failed for ${entityKey}:`, error);
      setDownloadStatus((prev) => ({ ...prev, [entityKey]: 'error' }));
      setToast({ message: `Failed to download ${entityKey}`, type: 'error' });

      setTimeout(() => {
        setDownloadStatus((prev) => ({ ...prev, [entityKey]: 'idle' }));
      }, 5000);
    }
  };

  const handleDownloadMerged = async () => {
    setMergedStatus('downloading');
    try {
      const response = await apiClient.get(
        '/api/v1/integration/fo-export/merged',
        { responseType: 'blob', timeout: 600000 }
      );

      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'Merged_Employee_View.xlsx';

      triggerBlobDownload(response.data as Blob, filename);

      setMergedStatus('done');
      setToast({ message: 'Merged team member view downloaded successfully', type: 'success' });

      setTimeout(() => setMergedStatus('idle'), 5000);
    } catch (error) {
      console.error('Merged download failed:', error);
      setMergedStatus('error');
      setToast({ message: 'Failed to download merged view', type: 'error' });
      setTimeout(() => setMergedStatus('idle'), 5000);
    }
  };

  const handleDownloadStaffList = async () => {
    setStaffListStatus('downloading');
    try {
      const response = await apiClient.get(
        '/api/v1/integration/fo-export/staff-list',
        { responseType: 'blob', timeout: 600000 }
      );

      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'Eduvos_Staff_List.xlsx';

      triggerBlobDownload(response.data as Blob, filename);

      setStaffListStatus('done');
      setToast({ message: 'Staff list downloaded successfully', type: 'success' });
      setTimeout(() => setStaffListStatus('idle'), 5000);
    } catch (error) {
      console.error('Staff list download failed:', error);
      setStaffListStatus('error');
      setToast({ message: 'Failed to download staff list', type: 'error' });
      setTimeout(() => setStaffListStatus('idle'), 5000);
    }
  };

  const handleDownloadBonusReport = async () => {
    const cycle = bonusCycles?.find((c) => `${c.review_type}:${c.year}` === bonusCycleKey);
    if (!cycle) return;
    setBonusReportStatus('downloading');
    try {
      await adminService.downloadBonusReport(cycle);
      setBonusReportStatus('done');
      setToast({ message: 'Bonus report downloaded successfully', type: 'success' });
      setTimeout(() => setBonusReportStatus('idle'), 5000);
    } catch (error) {
      console.error('Bonus report download failed:', error);
      setBonusReportStatus('error');
      // 502: Sage, F&O or the directory did not answer, and the report refuses
      // to arrive with a column missing. Worth saying so, since a retry helps.
      const status = (error as { response?: { status?: number } })?.response?.status;
      setToast({
        message:
          status === 502
            ? 'The bonus report could not reach Sage or F&O just now. Please try again in a few minutes.'
            : 'Failed to download the bonus report',
        type: 'error',
      });
      setTimeout(() => setBonusReportStatus('idle'), 5000);
    }
  };

  const handleDownloadAll = async () => {
    for (const entity of entities) {
      if (downloadStatus[entity.key] === 'downloading') continue;
      await handleDownloadEntity(entity.key);
    }
  };

  const handleDownloadProfessionalDetails = async () => {
    setProfessionalExportStatus('downloading');
    try {
      await adminService.downloadProfessionalDetailsExport();
      setProfessionalExportStatus('done');
      setToast({ message: 'Professional details exported successfully', type: 'success' });
      setTimeout(() => setProfessionalExportStatus('idle'), 5000);
    } catch (error) {
      console.error('Professional details export failed:', error);
      setProfessionalExportStatus('error');
      setToast({ message: 'Failed to export professional details', type: 'error' });
      setTimeout(() => setProfessionalExportStatus('idle'), 5000);
    }
  };

  const handleDownloadLineManagers = async () => {
    setLineManagerExportStatus('downloading');
    try {
      await adminService.downloadLineManagerExport();
      setLineManagerExportStatus('done');
      setToast({ message: 'Line people leader export downloaded successfully', type: 'success' });
      setTimeout(() => setLineManagerExportStatus('idle'), 5000);
    } catch (error) {
      console.error('Line manager export failed:', error);
      setLineManagerExportStatus('error');
      setToast({ message: 'Failed to export line people leaders', type: 'error' });
      setTimeout(() => setLineManagerExportStatus('idle'), 5000);
    }
  };

  const handleDownloadPositionGrades = async () => {
    setPositionGradeExportStatus('downloading');
    try {
      await adminService.downloadPositionGradeExport();
      setPositionGradeExportStatus('done');
      setToast({ message: 'Position & grade export downloaded successfully', type: 'success' });
      setTimeout(() => setPositionGradeExportStatus('idle'), 5000);
    } catch (error) {
      console.error('Position grade export failed:', error);
      setPositionGradeExportStatus('error');
      setToast({ message: 'Failed to export position & grade data', type: 'error' });
      setTimeout(() => setPositionGradeExportStatus('idle'), 5000);
    }
  };

  const handleDownloadHeadcount = async () => {
    setHeadcountExportStatus('downloading');
    try {
      await adminService.downloadHeadcountExport();
      setHeadcountExportStatus('done');
      setToast({ message: 'Headcount export downloaded successfully', type: 'success' });
      setTimeout(() => setHeadcountExportStatus('idle'), 5000);
    } catch (error) {
      console.error('Headcount export failed:', error);
      setHeadcountExportStatus('error');
      setToast({ message: 'Failed to export headcount data', type: 'error' });
      setTimeout(() => setHeadcountExportStatus('idle'), 5000);
    }
  };

  const handleDownloadOnboardingObjectives = async () => {
    setOnboardingObjExportStatus('downloading');
    try {
      await adminService.downloadOnboardingObjectivesExport();
      setOnboardingObjExportStatus('done');
      setToast({ message: 'Onboarding & objectives export downloaded successfully', type: 'success' });
      setTimeout(() => setOnboardingObjExportStatus('idle'), 5000);
    } catch (error) {
      console.error('Onboarding objectives export failed:', error);
      setOnboardingObjExportStatus('error');
      setToast({ message: 'Failed to export onboarding & objectives', type: 'error' });
      setTimeout(() => setOnboardingObjExportStatus('idle'), 5000);
    }
  };

  const handleDownloadCheckInCalibration = async () => {
    setCheckInCalibStatus('downloading');
    try {
      await adminService.downloadCheckInCalibrationExport(checkInCalibPeriod);
      setCheckInCalibStatus('done');
      setToast({ message: 'Check-in & calibration statistics exported successfully', type: 'success' });
      setTimeout(() => setCheckInCalibStatus('idle'), 5000);
    } catch (error) {
      console.error('Check-in & calibration export failed:', error);
      setCheckInCalibStatus('error');
      setToast({ message: 'Failed to export check-in & calibration statistics', type: 'error' });
      setTimeout(() => setCheckInCalibStatus('idle'), 5000);
    }
  };

  const isAnyDownloading =
    Object.values(downloadStatus).includes('downloading') || mergedStatus === 'downloading' || professionalExportStatus === 'downloading' || lineManagerExportStatus === 'downloading' || onboardingObjExportStatus === 'downloading' || positionGradeExportStatus === 'downloading' || headcountExportStatus === 'downloading' || staffListStatus === 'downloading' || checkInCalibStatus === 'downloading' || bonusReportStatus === 'downloading';

  const getStatusIcon = (status: DownloadStatus) => {
    switch (status) {
      case 'downloading':
        return <Loader2 className="w-4 h-4 animate-spin text-heritage-blue" />;
      case 'done':
        return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
      case 'error':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-heritage-blue" />
        <span className="ml-3 text-gray-600">Loading entity catalog...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Staff List Export (Sage take-on) */}
      {can(EXPORT_KEYS.staffList) && (
      <Card className="p-5 border-heritage-blue/40 bg-heritage-blue/[0.03]">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-heritage-blue/10 rounded-lg">
              <FileSpreadsheet className="w-5 h-5 text-heritage-blue" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Staff List (Sage Take-On Format)</h3>
              <p className="text-sm text-gray-600 mt-1">
                HR's full monthly staff list in the 39-column Sage take-on layout — live from Sage
                (core, contacts, hierarchy &amp; period salary) enriched with F&amp;O contract-end dates.
                Includes active, terminated &amp; archived employees. May take 1–3 minutes.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  'Company', 'Employee Code', 'Names', 'Nature of Contract', 'Job Title', 'Job Grade',
                  'Reports To', 'Cell', 'Email', 'ID / Passport', 'Gender', 'Age', 'Racial Group',
                  'Foreign Ind.', 'Qualification', 'Faculty', 'Campus', 'Closest Campus', 'Dept', 'Location',
                  'Period Salary', 'Date Engaged', 'Termination', 'Contract End', 'Is Manager?',
                ].map((field) => (
                  <span key={field} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                    {field}
                  </span>
                ))}
              </div>
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                Contains ID numbers, salaries &amp; race — restricted to Admin / HR (POPIA).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {staffListStatus !== 'idle' && getStatusIcon(staffListStatus)}
            <Button
              onClick={handleDownloadStaffList}
              disabled={staffListStatus === 'downloading' || isAnyDownloading}
              className="bg-heritage-blue hover:bg-heritage-blue/90"
            >
              {staffListStatus === 'downloading' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {staffListStatus === 'downloading' ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {/* Bonus Report — Finance's bonus-calculation extract */}
      {canBonusReport && (
      <Card className="p-5 border-heritage-blue/40 bg-heritage-blue/[0.03]">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-heritage-blue/10 rounded-lg">
              <HandCoins className="w-5 h-5 text-heritage-blue" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Bonus Report</h3>
              <p className="text-sm text-gray-600 mt-1">
                Everyone employed today (on staff in Sage or employed in F&amp;O), in the column order
                Finance uses for bonus calculations: the annual TGP from Sage, the performance rating from
                the review cycle you choose, contract dates, job title and grade. A separate sheet lists
                anyone Sage and F&amp;O disagree about. Live from Sage, F&amp;O and the platform. May take
                1–3 minutes.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  'Employee Number', 'Name', 'Date Engaged', 'Contract End', 'Type of Contract',
                  'Job Title', 'TGP (Annual)', 'Performance Rating', 'Campus', 'Function', 'Email', 'Job Grade',
                ].map((field) => (
                  <span key={field} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                    {field}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <label htmlFor="bonus-report-cycle" className="text-sm text-gray-700 shrink-0">
                  Rating from
                </label>
                {bonusCyclesFailed ? (
                  <span className="text-sm text-red-600">Couldn&apos;t load the review cycles. Refresh to try again.</span>
                ) : bonusCycles === null ? (
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading review cycles…
                  </span>
                ) : bonusCycles.length === 0 ? (
                  <span className="text-sm text-gray-500">No review cycle has ratings yet.</span>
                ) : (
                  <select
                    id="bonus-report-cycle"
                    value={bonusCycleKey}
                    onChange={(e) => setBonusCycleKey(e.target.value)}
                    disabled={bonusReportStatus === 'downloading'}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-heritage-blue"
                  >
                    {bonusCycles.map((c) => (
                      <option key={`${c.review_type}:${c.year}`} value={`${c.review_type}:${c.year}`}>
                        {c.label} — {c.completed.toLocaleString('en-ZA')} reviewed, {c.calibrated.toLocaleString('en-ZA')} calibrated
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                Contains annual packages &amp; performance ratings — restricted to Admin / HR and people given this export.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {bonusReportStatus !== 'idle' && getStatusIcon(bonusReportStatus)}
            <Button
              onClick={handleDownloadBonusReport}
              disabled={!bonusCycleKey || bonusReportStatus === 'downloading' || isAnyDownloading}
              className="bg-heritage-blue hover:bg-heritage-blue/90"
            >
              {bonusReportStatus === 'downloading' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {bonusReportStatus === 'downloading' ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {/* Professional Details Export */}
      {can(EXPORT_KEYS.professionalDetails) && (
      <Card className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-heritage-blue/10 rounded-lg">
              <GraduationCap className="w-5 h-5 text-heritage-blue" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Professional Details Export</h3>
              <p className="text-sm text-gray-600 mt-1">
                What each person has captured on Impact &amp; Growth: qualifications, work history and expertise,
                with how complete their profile is. It also checks each person&apos;s highest qualification against
                the title, education and NQF level F&amp;O holds, so you can see who has a doctorate but is not
                titled Dr. Generates an Excel file with 6 sheets.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  'Employee ID', 'Name', 'Campus', 'Office', 'Department', 'Function',
                  'Faculty', 'Job Family', 'Team 1', 'Team 2', 'Team 3', 'Position ID', 'Position Title', 'Reports To', 'Line Manager',
                  'Qualifications', 'Work History', 'Expertise', 'Completion %',
                  'Highest Qualification', 'F&O Title', 'F&O Education', 'F&O NQF Level', 'Flags',
                ].map((field) => (
                  <span key={field} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                    {field}
                  </span>
                ))}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Sheet 1: Completion Status (one row per person)
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Sheets 2-4: Qualifications, Career History, Expertise (every entry)
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Sheet 5: I&amp;G vs F&amp;O (people whose records differ)
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Sheet 6: About
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {professionalExportStatus !== 'idle' && getStatusIcon(professionalExportStatus)}
            <Button
              onClick={handleDownloadProfessionalDetails}
              disabled={professionalExportStatus === 'downloading' || isAnyDownloading}
              className="bg-heritage-blue hover:bg-heritage-blue/90"
            >
              {professionalExportStatus === 'downloading' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {professionalExportStatus === 'downloading' ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {/* Line Manager Export */}
      {can(EXPORT_KEYS.lineManager) && (
      <Card className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-heritage-blue/10 rounded-lg">
              <Users className="w-5 h-5 text-heritage-blue" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Line People Leader Export</h3>
              <p className="text-sm text-gray-600 mt-1">
                Export all people leaders (users with direct reports) including their organisational details
                and team size. Generates a single-sheet Excel file.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  'Employee ID', 'Name', 'Surname', 'Email', 'Campus',
                  'Functional Area', 'Department', 'Team 1', 'Team 2', 'Team 3', 'Job Title', 'Position Title', 'Direct Reports',
                ].map((field) => (
                  <span key={field} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                    {field}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {lineManagerExportStatus !== 'idle' && getStatusIcon(lineManagerExportStatus)}
            <Button
              onClick={handleDownloadLineManagers}
              disabled={lineManagerExportStatus === 'downloading' || isAnyDownloading}
              className="bg-heritage-blue hover:bg-heritage-blue/90"
            >
              {lineManagerExportStatus === 'downloading' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {lineManagerExportStatus === 'downloading' ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {/* Onboarding & Objectives Export */}
      {can(EXPORT_KEYS.onboardingObjectives) && (
      <Card className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-heritage-blue/10 rounded-lg">
              <ClipboardList className="w-5 h-5 text-heritage-blue" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Onboarding & Objectives Export</h3>
              <p className="text-sm text-gray-600 mt-1">
                Onboarding completion status and objective-setting statistics per user,
                with a summary breakdown by Job Family. Generates an Excel file with 2 sheets.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  'Employee ID', 'Name', 'Campus', 'Department', 'Function',
                  'Job Family', 'Team 1', 'Team 2', 'Team 3', 'Onboarding Stage', 'Profile %', 'Objectives Count',
                  'Active Objectives', 'Collab Sessions', 'Collab Completed',
                ].map((field) => (
                  <span key={field} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                    {field}
                  </span>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Sheet 1: Onboarding & Objectives (one row per user)
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Sheet 2: By Job Family (summary)
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {onboardingObjExportStatus !== 'idle' && getStatusIcon(onboardingObjExportStatus)}
            <Button
              onClick={handleDownloadOnboardingObjectives}
              disabled={onboardingObjExportStatus === 'downloading' || isAnyDownloading}
              className="bg-heritage-blue hover:bg-heritage-blue/90"
            >
              {onboardingObjExportStatus === 'downloading' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {onboardingObjExportStatus === 'downloading' ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {/* Check-in & Calibration Statistics Export */}
      {can(EXPORT_KEYS.checkinCalibration) && (
      <Card className="p-5 border-heritage-blue/40 bg-heritage-blue/[0.03]">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-heritage-blue/10 rounded-lg">
              <ClipboardCheck className="w-5 h-5 text-heritage-blue" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Check-in & Calibration Statistics</h3>
              <p className="text-sm text-gray-600 mt-1">
                Check-in and calibration status for the chosen period — who has (and hasn&apos;t)
                completed, so you can follow up by function. Counts only the people in scope: not
                anyone Too Soon To Rate, and at Year-End not the ring-fenced titles; they are listed
                on a third sheet. Includes the 5 check-in ratings (overall + 4 KPAs) and the 5
                post-calibration ratings.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  'Employee ID', 'Name', 'Job Title', 'Function', 'Department', 'Campus',
                  'Line Manager', 'Manager Job Title',
                  'Check-in (Y/N)', 'Overall + 4 KPA Ratings',
                  'Calibration (Y/N)', 'Overall + 4 Post-Cal Ratings',
                ].map((field) => (
                  <span key={field} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                    {field}
                  </span>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Sheet 1: Employees (one row per person)
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Sheet 2: By Function (completion summary)
                </span>
                <span className="flex items-center gap-1">
                  <FileText className="w-3 h-3" />
                  Sheet 3: Left out (and why)
                </span>
              </div>
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                Contains performance ratings — restricted to Admin / HR (POPIA).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {checkInCalibStatus !== 'idle' && getStatusIcon(checkInCalibStatus)}
            <select
              aria-label="Check-in period"
              className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
              value={checkInCalibPeriod}
              onChange={(e) => setCheckInCalibPeriod(e.target.value as 'midyear' | 'yearend')}
              disabled={checkInCalibStatus === 'downloading'}
            >
              <option value="midyear">Mid-Year</option>
              <option value="yearend">Year-End</option>
            </select>
            <Button
              onClick={handleDownloadCheckInCalibration}
              disabled={checkInCalibStatus === 'downloading' || isAnyDownloading}
              className="bg-heritage-blue hover:bg-heritage-blue/90"
            >
              {checkInCalibStatus === 'downloading' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {checkInCalibStatus === 'downloading' ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {/* Position & Grade Export */}
      {can(EXPORT_KEYS.positionGrade) && (
      <Card className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-heritage-blue/10 rounded-lg">
              <BarChart3 className="w-5 h-5 text-heritage-blue" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Position & Grade Export</h3>
              <p className="text-sm text-gray-600 mt-1">
                Export all team members (active &amp; terminated) with their position details and grade level (e.g. B1–F).
                Generates a single-sheet Excel file.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  'Employee ID', 'Name', 'Campus', 'Job Family', 'Position Type',
                  'Status', 'Position ID', 'Position Title', 'Grade Level',
                  'Functional Area', 'Department', 'Team 1', 'Team 2', 'Team 3', 'Line People Leader',
                ].map((field) => (
                  <span key={field} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                    {field}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {positionGradeExportStatus !== 'idle' && getStatusIcon(positionGradeExportStatus)}
            <Button
              onClick={handleDownloadPositionGrades}
              disabled={positionGradeExportStatus === 'downloading' || isAnyDownloading}
              className="bg-heritage-blue hover:bg-heritage-blue/90"
            >
              {positionGradeExportStatus === 'downloading' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {positionGradeExportStatus === 'downloading' ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {/* Headcount Export */}
      {can(EXPORT_KEYS.headcount) && (
      <Card className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Headcount Export</h3>
              <p className="text-sm text-gray-600 mt-1">
                Pure F&O headcount report — all data sourced live from Dynamics F&O.
                Excludes ICAs (Permanent and FTC only). May take 2-3 minutes.
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {[
                  'Employee Code', 'Name', 'Race', 'Gender', 'RG', 'Age',
                  'Disabled', 'Job Title', 'Role Title', 'Nature of Contract', 'Job Grade',
                  'Location', 'Municipality', 'Department',
                  'Date Engaged', 'Qualification', 'EE Cross-Tabs',
                ].map((field) => (
                  <span key={field} className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">
                    {field}
                  </span>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">OFO codes are derived from F&amp;O Title assignments (199 of 511 titles mapped).</p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            {headcountExportStatus !== 'idle' && getStatusIcon(headcountExportStatus)}
            <Button
              onClick={handleDownloadHeadcount}
              disabled={headcountExportStatus === 'downloading' || isAnyDownloading}
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              {headcountExportStatus === 'downloading' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {headcountExportStatus === 'downloading' ? 'Generating...' : 'Download'}
            </Button>
          </div>
        </div>
      </Card>
      )}

      {canSeeRawFo && (
      <>
      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
        <div className="relative flex justify-center">
          <span className="bg-gray-50 px-3 text-xs font-medium text-gray-400 uppercase tracking-wider">F&O Data</span>
        </div>
      </div>

      {/* F&O Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">F&O Data Export</h2>
          <p className="text-gray-600 mt-1">
            Download team member data from Dynamics Finance & Operations as Excel files
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleDownloadAll}
            disabled={isAnyDownloading}
          >
            <Download className="w-4 h-4 mr-2" />
            Download All
          </Button>
          <Button
            className="bg-heritage-blue hover:bg-heritage-blue/90"
            onClick={handleDownloadMerged}
            disabled={isAnyDownloading}
          >
            {mergedStatus === 'downloading' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Layers className="w-4 h-4 mr-2" />
            )}
            {mergedStatus === 'downloading' ? 'Generating...' : 'Download Merged View'}
          </Button>
        </div>
      </div>

      {/* Merged View Info */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Layers className="w-5 h-5 text-heritage-blue" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900">Merged Team Member View</p>
            <p className="text-sm text-blue-800 mt-1">
              Combines Workers + Employments V2 + Positions + Employment Details into a single
              spreadsheet joined on Personnel Number. Ideal for a complete team member snapshot.
            </p>
          </div>
          {mergedStatus !== 'idle' && (
            <div className="ml-auto">{getStatusIcon(mergedStatus)}</div>
          )}
        </div>
      </Card>

      {/* Entity Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {entities.map((entity) => {
          const status = downloadStatus[entity.key] || 'idle';
          const icon = ENTITY_ICONS[entity.key] || <Database className="w-6 h-6" />;

          return (
            <Card
              key={entity.key}
              className={`p-5 hover:shadow-lg transition-shadow ${
                status === 'downloading' ? 'ring-2 ring-heritage-blue/30' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="p-2.5 bg-gray-100 rounded-lg text-heritage-blue">{icon}</div>
                {getStatusIcon(status)}
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-1">{entity.name}</h3>
              <p className="text-sm text-gray-600 mb-3">{entity.description}</p>

              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  ~{entity.estimated_fields} fields
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownloadEntity(entity.key)}
                  disabled={status === 'downloading' || isAnyDownloading}
                >
                  {status === 'downloading' ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Download className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {status === 'downloading' ? 'Exporting...' : 'Download'}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Info box */}
      <Card className="p-4 bg-gray-50 border-gray-200">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-700">About F&O Data Exports</p>
            <p className="text-sm text-gray-600 mt-1">
              Each export fetches live data directly from Dynamics F&O via OData API. Large
              entities (Workers, Positions) may take 1–2 minutes to download. The data is
              exported as-is from F&O with no transformations applied.
            </p>
          </div>
        </div>
      </Card>
      </>
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default FODataExportPage;
